// 📲 src/pages/AppUpdates.jsx
//
// Controls the in-app "Update available" prompt shown in the Oráku mobile apps.
// Writes a single Firestore doc `app_config/version`. The app compares its own
// build number against these on the home screen:
//   current < min     → forced update (blocking)
//   current < latest   → optional update (dismissible)
//
// Bump the "latest build" after you publish a new build to the store. Bump the
// "minimum build" only when you need to FORCE everyone onto a new version.

import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { PageHeader, Card, GoldBtn, Spinner } from '../components/UI';

const PLAY = 'https://play.google.com/store/apps/details?id=com.orakusmartai.oraku_smart_ai';
const APPSTORE = 'https://apps.apple.com/app/oraku-smart-ai/id6775992369';

// v2: VERSION-string comparison (e.g. "1.5.0") — build numbers proved
// unreliable across uploads (iOS numbering changed 30 → 16), which kept the
// update prompt showing even after users updated.
const DEFAULTS = {
  androidLatestVersion: '1.1.0', androidMinVersion: '1.0.0',
  iosLatestVersion: '1.5.0',     iosMinVersion: '1.0.0',
  androidUrl: PLAY, iosUrl: APPSTORE,
  message: '',
};

export default function AppUpdates() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'app_config', 'version'));
      if (snap.exists()) setForm({ ...DEFAULTS, ...snap.data() });
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  const num = (v) => Math.max(0, parseInt(v || '0', 10) || 0);

  async function save() {
    setSaving(true); setError(null);
    try {
      const clean = (v, fb) => ((v || '').toString().trim() || fb);
      await setDoc(doc(db, 'app_config', 'version'), {
        androidLatestVersion: clean(form.androidLatestVersion, '1.0.0'),
        androidMinVersion:    clean(form.androidMinVersion, '1.0.0'),
        iosLatestVersion:     clean(form.iosLatestVersion, '1.0.0'),
        iosMinVersion:        clean(form.iosMinVersion, '1.0.0'),
        androidUrl: (form.androidUrl || PLAY).trim(),
        iosUrl:     (form.iosUrl || APPSTORE).trim(),
        message:    (form.message || '').trim(),
        updatedAt:  serverTimestamp(),
      }, { merge: true });
      setSavedAt(new Date());
    } catch (e) { setError(e.message); }
    setSaving(false);
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (loading) return <div style={{ padding: 40 }}><Spinner /></div>;

  return (
    <div style={{ padding: 32, maxWidth: 760 }}>
      <PageHeader
        title="App Updates"
        subtitle="Control the in-app “Update available” prompt for the Oráku mobile apps."
      />

      <Card style={{ marginBottom: 20 }}>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.6, marginTop: 0 }}>
          The app reads its own <b>version number</b> (e.g. 1.5.0) and compares it
          here. If the user's version is below <b>Latest</b>, they see a dismissible
          “Update available” sheet. If it's below <b>Minimum</b>, they get a
          <b> forced</b> update they can't dismiss. After a new version goes LIVE
          on a store, set the matching <b>Latest version</b> here — never set it
          higher than what's actually downloadable, or updated users will keep
          seeing the prompt.
        </p>
      </Card>

      <Card title="Android (Google Play)" style={{ marginBottom: 18 }}>
        <Row label="Latest version (e.g. 1.1.0)"><Inp value={form.androidLatestVersion} onChange={set('androidLatestVersion')} /></Row>
        <Row label="Minimum (force) version"><Inp value={form.androidMinVersion} onChange={set('androidMinVersion')} /></Row>
        <Row label="Store URL"><Inp value={form.androidUrl} onChange={set('androidUrl')} /></Row>
      </Card>

      <Card title="iOS (App Store)" style={{ marginBottom: 18 }}>
        <Row label="Latest version (e.g. 1.5.0)"><Inp value={form.iosLatestVersion} onChange={set('iosLatestVersion')} /></Row>
        <Row label="Minimum (force) version"><Inp value={form.iosMinVersion} onChange={set('iosMinVersion')} /></Row>
        <Row label="Store URL"><Inp value={form.iosUrl} onChange={set('iosUrl')} /></Row>
      </Card>

      <Card title="Prompt message (optional)" style={{ marginBottom: 18 }}>
        <textarea
          value={form.message}
          onChange={set('message')}
          placeholder="e.g. We've added Pet Companion memory, faster chat and bug fixes."
          rows={3}
          style={{
            width: '100%', background: '#0F1828', color: '#fff',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10,
            padding: 12, fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical',
          }}
        />
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, margin: '8px 0 0' }}>
          Leave blank to use the default wording.
        </p>
      </Card>

      {error && <div style={{ color: '#ff6b6b', fontSize: 13, marginBottom: 12 }}>⚠ {error}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <GoldBtn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save & publish'}</GoldBtn>
        {savedAt && <span style={{ color: '#008751', fontSize: 13 }}>✓ Saved {savedAt.toLocaleTimeString()}</span>}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
      <label style={{ width: 200, color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>{label}</label>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}
function Inp({ value, onChange, type = 'text' }) {
  return (
    <input
      value={value} onChange={onChange} type={type}
      style={{
        width: '100%', background: '#0F1828', color: '#fff',
        border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10,
        padding: '10px 12px', fontSize: 13.5,
      }}
    />
  );
}
